import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Lock as LockIcon,
  LocalOffer as LocalOfferIcon,
  Public as PublicIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeAccionFacturacion,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import ReglasImpuestosVentas from 'productosnoserializados/ReglasImpuestosVentas';
import DescuentosGenerales from './DescuentosGenerales';
import AreaGeograficaVentas from './AreaGeograficaVentas';
import tiposDocumentosData from 'utils/tiposDocumentos.json';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

/** Pantalla agrupadora "Reglas de ventas" / configuración fiscal. */
export const PROGRAMA_REGLAS_VENTAS = 'regladeventas';
const PROGRAMA_REGLAS_IMPUESTOS = 'reglasimpuestosventas';
const PROGRAMA_AREAS_GEOGRAFICAS = 'areasgeograficaventas';
const PROGRAMA_DESCUENTOS_GENERALES = 'descuentosgenerales';

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const tiposAnchosColumnas = ['16%', '22%', '11%', '10%', '10%', '10%', '10%', '11%'];
const tiposColgroup = (
  <colgroup>
    {tiposAnchosColumnas.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);
const tiposTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  minWidth: { xs: 820, sm: 980 },
};

const ConfiguracionDocumentosFiscales = () => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_REGLAS_VENTAS),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeAccionFacturacion(roleDetalles, PROGRAMA_REGLAS_VENTAS, 'modificar'),
    [roleDetalles]
  );
  const puedeBorrar = useMemo(
    () => puedeAccionFacturacion(roleDetalles, PROGRAMA_REGLAS_VENTAS, 'borrar'),
    [roleDetalles]
  );
  const tienePermisoReglasImpuestos = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_REGLAS_IMPUESTOS),
    [roleDetalles]
  );
  const tienePermisoAreasGeograficas = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_AREAS_GEOGRAFICAS),
    [roleDetalles]
  );
  const tienePermisoDescuentosGenerales = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_DESCUENTOS_GENERALES),
    [roleDetalles]
  );

  const [tabPrincipal, setTabPrincipal] = useState(0);

  const tabMapping = useMemo(() => {
    const map = ['documentos'];
    if (tienePermisoReglasImpuestos) map.push('impuestos');
    if (tienePermisoDescuentosGenerales) map.push('descuentos');
    if (tienePermisoAreasGeograficas) map.push('areas');
    return map;
  }, [tienePermisoReglasImpuestos, tienePermisoDescuentosGenerales, tienePermisoAreasGeograficas]);

  const tabActual = tabMapping[tabPrincipal] || 'documentos';
  const reglasImpuestosRef = useRef(null);
  const descuentosGeneralesRef = useRef(null);
  const puedeModificarImpuestos = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_REGLAS_IMPUESTOS),
    [roleDetalles]
  );
  const puedeModificarDescuentos = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_DESCUENTOS_GENERALES),
    [roleDetalles]
  );

  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [tipoEditando, setTipoEditando] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [nombresDocumentos] = useState(() => {
    const documentos = [];
    tiposDocumentosData[0].documentosfiscal.forEach((categoria) => {
      documentos.push(categoria);
    });

    return documentos;
  });

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [secuenciaDesde, setSecuenciaDesde] = useState('1');
  const [secuenciaHasta, setSecuenciaHasta] = useState('999999999');
  const [secuenciaActual, setSecuenciaActual] = useState('1');
  const [fechaValida, setFechaValida] = useState('');
  const [activo, setActivo] = useState('S');

  const cargarTiposDocumentos = useCallback(async () => {
    if (!usuarioLog) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_listartiposdocumentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setTiposDocumentos(data.tipos || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar tipos de documentos');
      }
    } catch {
      showError('Error de conexión al cargar tipos de documentos');
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (puedeLeer) cargarTiposDocumentos();
  }, [puedeLeer, cargarTiposDocumentos]);

  const handleAbrirDialogo = (tipo = null) => {
    if (!puedeModificar) {
      showError('No tiene permisos para modificar tipos de documentos.');
      return;
    }
    if (tipo) {
      setModoEdicion(true);
      setTipoEditando(tipo);
      setNombre(tipo.nombre || '');
      setDescripcion(tipo.descripcion || '');
      setSecuenciaDesde(tipo.secuencia_desde?.toString() || '1');
      setSecuenciaHasta(tipo.secuencia_hasta?.toString() || '999999999');
      setSecuenciaActual(tipo.secuencia_actual?.toString() || '1');
      setFechaValida(tipo.fecha_valida || '');
      setActivo(tipo.activo || 'S');
    } else {
      setModoEdicion(false);
      setTipoEditando(null);
      setNombre('');
      setDescripcion('');
      setSecuenciaDesde('1');
      setSecuenciaHasta('999999999');
      setSecuenciaActual('1');
      setFechaValida(new Date().toISOString().split('T')[0]);
      setActivo('S');
    }
    setDialogoAbierto(true);
  };

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    setModoEdicion(false);
    setTipoEditando(null);
    setNombre('');
    setDescripcion('');
    setSecuenciaDesde('1');
    setSecuenciaHasta('999999999');
    setSecuenciaActual('1');
    setFechaValida('');
    setActivo('S');
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar tipos de documentos.');
      return;
    }
    if (!nombre.trim()) {
      showError('El nombre del tipo de documento es requerido');
      return;
    }
    if (!fechaValida) {
      showError('La fecha válida es requerida');
      return;
    }
    const fechaRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!fechaRegex.test(fechaValida)) {
      showError('La fecha válida debe tener el formato YYYY-MM-DD');
      return;
    }
    const desde = parseInt(secuenciaDesde, 10);
    const hasta = parseInt(secuenciaHasta, 10);
    const actual = parseInt(secuenciaActual, 10);
    if (isNaN(desde) || isNaN(hasta) || isNaN(actual)) {
      showError('Las secuencias deben ser números válidos');
      return;
    }
    if (desde >= hasta) {
      showError('La secuencia "desde" debe ser menor que la secuencia "hasta"');
      return;
    }
    if (actual < desde || actual > hasta) {
      showError('La secuencia actual debe estar entre "desde" y "hasta"');
      return;
    }

    try {
      const tipoData = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        secuencia_desde: desde,
        secuencia_hasta: hasta,
        secuencia_actual: actual,
        fecha_valida: fechaValida,
        activo,
      };
      if (modoEdicion && tipoEditando) tipoData.id = tipoEditando.id;

      const response = await fetch(servidor + '/api/facturabackend_guardartipodocumento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          tipo_documento: tipoData,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Tipo de documento guardado exitosamente');
        handleCerrarDialogo();
        cargarTiposDocumentos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al guardar el tipo de documento');
      }
    } catch {
      showError('Error de conexión al guardar');
    }
  };

  const handleCerrarDocumento = async (tipo) => {
    if (!puedeModificar) {
      showError('No tiene permisos para cerrar tipos de documentos.');
      return;
    }
    const mensajeConfirmacion = `¿Está seguro de cerrar la secuencia ACTIVA "${tipo.nombre}"?\n\nEsta acción cambiará el estado a "Cerrado" y no podrá volver a activarse.`;
    if (!window.confirm(mensajeConfirmacion)) return;

    try {
      const response = await fetch(servidor + '/api/facturabackend_cerrartipodocumento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          tipo_id: tipo.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Documento cerrado exitosamente');
        cargarTiposDocumentos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cerrar el documento');
      }
    } catch {
      showError('Error de conexión al cerrar el documento');
    }
  };

  const handleEliminar = async (tipo) => {
    if (!puedeBorrar) {
      showError('No tiene permisos para eliminar tipos de documentos.');
      return;
    }
    if (tipo.activo === 'N') {
      showError('No se pueden eliminar secuencias cerradas (inactivas)');
      return;
    }
    const mensajeConfirmacion =
      tipo.activo === 'S'
        ? `¿Está seguro de eliminar el tipo de secuencia ACTIVO "${tipo.nombre}"?\n\nEsta acción no se puede deshacer.`
        : `¿Está seguro de eliminar el tipo de documento "${tipo.nombre}"?`;
    if (!window.confirm(mensajeConfirmacion)) return;

    try {
      const response = await fetch(servidor + '/api/facturabackend_eliminartipodocumento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          tipo_id: tipo.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Tipo de documento eliminado exitosamente');
        cargarTiposDocumentos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al eliminar el tipo de documento');
      }
    } catch {
      showError('Error de conexión al eliminar');
    }
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 6 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <ReceiptIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder a la configuración fiscal.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Configuración fiscal"
        subtitle="Secuencias fiscales, reglas de impuestos, descuentos y áreas geográficas"
        cta={
          tabActual === 'documentos' ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAbrirDialogo()}
              disabled={!puedeModificar || !usuarioLog}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Nuevo tipo de documento
            </Button>
          ) : tabActual === 'impuestos' ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => reglasImpuestosRef.current?.abrirNuevaRegla()}
              disabled={!puedeModificarImpuestos || !usuarioLog}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Nueva regla
            </Button>
          ) : tabActual === 'descuentos' ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => descuentosGeneralesRef.current?.abrirNuevaRegla()}
              disabled={!puedeModificarDescuentos || !usuarioLog}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Nueva regla
            </Button>
          ) : null
        }
      />

      <Card sx={{ ...cardShellSx, mt: 2 }}>
        <Box sx={{ borderBottom: `1px solid ${FP.color.border}`, px: { xs: 1, md: 2 } }}>
          <Tabs
            value={tabPrincipal}
            onChange={(_, v) => setTabPrincipal(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.88rem',
                minHeight: 48,
                color: FP.color.textSecondary,
                gap: 0.6,
              },
              '& .Mui-selected': { color: FP.color.navy },
              '& .MuiTabs-indicator': {
                backgroundColor: FP.color.navy,
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab icon={<ReceiptIcon />} iconPosition="start" label="Documentos fiscales" />
            {tienePermisoReglasImpuestos && (
              <Tab icon={<AccountBalanceIcon />} iconPosition="start" label="Reglas de impuestos" />
            )}
            {tienePermisoDescuentosGenerales && (
              <Tab icon={<LocalOfferIcon />} iconPosition="start" label="Descuentos generales" />
            )}
            {tienePermisoAreasGeograficas && (
              <Tab icon={<PublicIcon />} iconPosition="start" label="Área geográfica" />
            )}
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {tabActual === 'documentos' ? (
            <>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                  Tipos de secuencias definidos
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                  Lista de todos los tipos de secuencias configurados. Puede modificar o eliminar el tipo
                  activo desde esta tabla.
                </Typography>
              </Stack>

              {cargando ? (
                <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                  Cargando…
                </Typography>
              ) : tiposDocumentos.length === 0 ? (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${FP.color.border}`,
                    bgcolor: FP.color.infoSoft,
                  }}
                >
                  No hay tipos de documentos configurados. Haga clic en "Nuevo tipo de documento" para crear
                  uno.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    overflow: 'auto',
                  }}
                >
                  <Table size="small" sx={tiposTableSx}>
                    {tiposColgroup}
                    <TableBody>
                      <TableRow
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          '& td': {
                            border: 'none',
                            borderBottom: `2px solid ${FP.color.border}`,
                            bgcolor: FP.color.surfaceAlt,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography component="span" sx={cellHeadSx}>
                            Nombre
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={cellHeadSx}>
                            Descripción
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                          >
                            Fecha válida
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                          >
                            Sec. desde
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                          >
                            Sec. hasta
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                          >
                            Sec. actual
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                          >
                            Estado
                          </Typography>
                        </TableCell>
                        <TableCell className="no-print">
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                          >
                            Acciones
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {tiposDocumentos.map((tipo) => {
                        const esActivo = tipo.activo === 'S';
                        return (
                          <TableRow
                            key={tipo.id}
                            hover
                            sx={{
                              backgroundColor: esActivo ? FP.color.accentBlueSoft : 'transparent',
                              borderLeft: esActivo
                                ? `4px solid ${FP.color.accentBlue}`
                                : '4px solid transparent',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {esActivo && (
                                  <Chip
                                    label="ACTIVO"
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.65rem',
                                      height: 20,
                                      bgcolor: FP.color.successSoft,
                                      color: FP.color.successDark,
                                    }}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    fontWeight: esActivo ? 700 : 500,
                                    color: FP.color.textPrimary,
                                    fontSize: '0.88rem',
                                  }}
                                >
                                  {tipo.nombre}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                              {tipo.descripcion || '-'}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}
                            >
                              {tipo.fecha_valida
                                ? new Date(tipo.fecha_valida).toLocaleDateString('es-ES')
                                : '-'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: FP.color.textPrimary,
                                fontSize: '0.85rem',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {tipo.secuencia_desde?.toLocaleString()}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: FP.color.textPrimary,
                                fontSize: '0.85rem',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {tipo.secuencia_hasta?.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={tipo.secuencia_actual?.toLocaleString()}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: esActivo ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                                  color: esActivo ? FP.color.accentBlue : FP.color.textSecondary,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={esActivo ? 'Activo' : 'Cerrado'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: esActivo ? FP.color.successSoft : FP.color.surfaceAlt,
                                  color: esActivo ? FP.color.successDark : FP.color.textSecondary,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" className="no-print">
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirDialogo(tipo)}
                                disabled
                                sx={{ color: FP.color.accentBlue }}
                                title="Modificar tipo de secuencia"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {esActivo && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleCerrarDocumento(tipo)}
                                  disabled={!puedeModificar || !usuarioLog}
                                  sx={{ color: FP.color.warning }}
                                  title="Cerrar secuencia activa"
                                >
                                  <LockIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleEliminar(tipo)}
                                disabled
                                sx={{
                                  color: esActivo ? FP.color.danger : FP.color.textMuted,
                                  '&.Mui-disabled': { color: FP.color.textMuted },
                                }}
                                title={
                                  esActivo
                                    ? 'Eliminar tipo de secuencia activo'
                                    : 'No se pueden eliminar secuencias cerradas'
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : tabActual === 'impuestos' ? (
            <Box sx={{ mt: 0.5 }}>
              <ReglasImpuestosVentas
                ref={reglasImpuestosRef}
                embedded
                programaPermiso={PROGRAMA_REGLAS_IMPUESTOS}
                usarPermisoFacturacion
              />
            </Box>
          ) : tabActual === 'descuentos' ? (
            <Box sx={{ mt: 0.5 }}>
              <DescuentosGenerales ref={descuentosGeneralesRef} embedded />
            </Box>
          ) : tabActual === 'areas' ? (
            <Box sx={{ mt: 0.5 }}>
              <AreaGeograficaVentas />
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {tabActual === 'documentos' && (
        <Dialog
          open={dialogoAbierto}
          onClose={handleCerrarDialogo}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              boxShadow: FP.shadow.md,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              {modoEdicion ? 'Editar tipo de documento' : 'Nuevo tipo de documento'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required size="small" sx={fpInputSx}>
                  <InputLabel shrink>Nombre</InputLabel>
                  <Select
                    notched
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    label="Nombre"
                    disabled={!puedeModificar}
                  >
                    {nombresDocumentos.map((nombreDoc) => (
                      <MenuItem key={nombreDoc} value={nombreDoc}>
                        {nombreDoc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  multiline
                  rows={2}
                  helperText="Descripción opcional del tipo de documento"
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Fecha válida"
                  type="date"
                  value={fechaValida}
                  onChange={(e) => setFechaValida(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  helperText="Fecha de validez de la secuencia"
                  disabled={!puedeModificar}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Secuencia desde"
                  type="number"
                  value={secuenciaDesde}
                  onChange={(e) => setSecuenciaDesde(e.target.value)}
                  required
                  inputProps={{ min: 1 }}
                  helperText="Número inicial de la secuencia"
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Secuencia hasta"
                  type="number"
                  value={secuenciaHasta}
                  onChange={(e) => setSecuenciaHasta(e.target.value)}
                  required
                  inputProps={{ min: 1 }}
                  helperText="Número final de la secuencia"
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Secuencia actual"
                  type="number"
                  value={secuenciaActual}
                  onChange={(e) => setSecuenciaActual(e.target.value)}
                  required
                  inputProps={{ min: 1 }}
                  helperText="Número actual de la secuencia"
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Estado"
                  value={activo}
                  onChange={(e) => setActivo(e.target.value)}
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="S">Activo</MenuItem>
                  <MenuItem value="N">Inactivo</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.5,
              borderTop: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              gap: 1,
            }}
          >
            <Button
              onClick={handleCerrarDialogo}
              startIcon={<CloseIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: FP.color.textSecondary,
                borderRadius: 1.4,
                '&:hover': { bgcolor: FP.color.surfaceAlt },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleGuardar}
              disabled={!puedeModificar || !usuarioLog}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              {modoEdicion ? 'Modificar' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ConfiguracionDocumentosFiscales;
